import { nanoid } from 'nanoid';
import { State } from '@autopylot-internal/state';
import { ILink, WithLinks } from '@autopylot-internal/autopylot-api-client';
import { INote, INoteResponse, NotesClient } from './NotesClient.ts';
import { NewNoteViewModel } from './NewNoteViewModel.ts';
import { EditNoteViewModel } from './EditNoteViewModel.ts';
import { NoteValidator } from './NoteValidator.ts';

export interface IWithNotesLink {
    links: { notes: ILink }
}

export class NotesModel<T extends IWithNotesLink, R extends INoteResponse> extends State<Record<string, never>> {
    constructor(
        private entity: T,
        private client: NotesClient,
        private validator: NoteValidator
    ) {
        super({});
    };

    private notes: R[] = [];

    hasLoaded = false;

    load = async () => {
        const notes = await this.client.getNotes(this.entity.links.notes) as R[];
        this.notes = notes;
        this.hasLoaded = true;
        this.setState({});
    };

    addNewNote = async (note: INote) => {
        const response = await this.client.addNote({
            parentLink: this.entity.links.notes,
            note
        }) as R;

        // when running locally with bypass, note will already have been added via websocket
        // so only add it here if not already in the list
        if (!this.notes.some(n => n.noteId === response.noteId)) {
            this.notes.push(response);
            this.setState({});
        }

        return response;
    };

    putNote = async (note: WithLinks<INote>) => {
        const response = await this.client.updateNote(note);
        this.notes = [
            ...this.notes.filter(n => n.noteId !== note.noteId),
            response
        ];
        this.setState({});
    };

    deleteNote = async (note: WithLinks<INote>) => {
        await this.client.deleteNote(note);
        this.localDeleteNote(note);
    };

    localDeleteNote = (note: INote) => {
        this.notes = this.notes.filter(n => n.noteId !== note.noteId);

        // we mutated state, let subscribers know
        this.setState({});
    };

    getNotes = (): R[] => {
        return this.notes;
    };

    getNote = (noteId: string): R | undefined => {
        return this.getNotes().find(n => n.noteId === noteId);
    };

    getAttachmentCount = () => {
        return this.notes.reduce((count, note) => {
            return count + note.attachments.length;
        }, 0);
    };

    createNewNoteVM = (note: Partial<INote> = {}): NewNoteViewModel<T, R> => {
        const newNote: INote = {
            noteId: nanoid(),
            title: '',
            html: '',
            attachments: [],
            otherAttributes: {
                dateTime: new Date().toISOString(),
                type: 'other'
            },
            ...note,
        };

        return new NewNoteViewModel(newNote, this);
    };

    createEditNoteVM = async (noteId: string): Promise<EditNoteViewModel<T, R>> => {
        while (!this.hasLoaded) {
            await new Promise(resolve => setTimeout(resolve, 250));
        }

        const note = this.getNote(noteId);
        if (!note) {
            throw new Error('note not found');
        }

        return new EditNoteViewModel(note, this);
    };

    getValidator = () => this.validator;
}
